import { NUMERIC_TYPES } from 'constants/parser';

/**
 * Node kind discriminator values for the visual builder model.
 * @enum {string}
 */
export const NODE_KIND = {
  GROUP: 'group',
  COMPARISON: 'comparison',
  COLLECTION: 'collection',
  RELATIVE_DATE: 'relativeDate',
  SET_MEMBERSHIP: 'setMembership',
  RAW: 'raw',
  SWITCH: 'switch',
  FUNCTION: 'function',
  ARITHMETIC: 'arithmetic',
};

/**
 * Value model kind discriminator values.
 * A valueModel represents the value on either side of a comparison, a function
 * argument, or an arithmetic operand.
 * @enum {string}
 */
export const VALUE_KIND = {
  FIELD_REF: 'fieldRef',
  LITERAL: 'literal',
  FUNCTION: 'function',
  ARITHMETIC: 'arithmetic',
  /**
   * An array-function used as a scalar value.
   * Shape: { kind, fn, arraySource (valueModel), iteratorName, arg, argKind, id }
   * argKind: 'predicate' | 'selector' | 'none'
   */
  AGGREGATE: 'aggregate',
  /**
   * Member access on a value (e.g. MIN_BY(...).CreatedDate).
   * Shape: { kind, target (valueModel), property }
   */
  MEMBER_ACCESS: 'memberAccess',
  RAW: 'raw',
};

/**
 * Set-membership modes.
 *   noneOf  ⇢  AND(path != 'a', path != 'b', …)  — value is in NONE of the set
 *   anyOf   ⇢  OR(path == 'a', path == 'b', …)   — value is in ANY of the set
 *
 * @enum {string}
 */
export const SET_MEMBERSHIP_MODE = {
  NONE_OF: 'noneOf',
  ANY_OF: 'anyOf',
};

/**
 * Date arithmetic function names that the visual builder can represent as a
 * first-class relativeDate node.
 *
 * Shape: ADD_<UNIT>(field, amount) <comparator> DATE_NOW(nowArg?)
 */
export const DATE_FN_SET = new Set([
  'ADD_BUSINESS_DAYS',
  'ADD_DAYS',
  'ADD_MONTHS',
  'ADD_YEARS',
  'ADD_HOURS',
  'ADD_MINUTES',
]);

/**
 * Set of type strings emitted without single-quote wrapping.
 * Numbers and booleans are bare; everything else — strings, enums, guids, and
 * date LITERALS (the grammar requires `'YYYY-MM-DD'`; bare 2025-06-30 parses as
 * arithmetic) — is quoted.
 */
export const UNQUOTED_TYPES = new Set([...NUMERIC_TYPES, 'bool']);

/**
 * The full set of comparison operator strings the builder can represent
 * as a first-class comparison node.
 */
export const COMPARISON_OPERATORS = new Set([
  '==',
  '!=',
  '>=',
  '<=',
  '>',
  '<',
  'contains',
  'startsWith',
  'endsWith',
]);

/**
 * Logical group operators.
 * @enum {string}
 */
export const GROUP_OPERATORS = {
  AND: 'AND',
  OR: 'OR',
};

/**
 * Collection function names supported by the builder.
 * @enum {string}
 */
export const COLLECTION_FUNCTIONS = {
  ANY: 'ANY',
  ALL: 'ALL',
};

/**
 * Regex matching an array type string in both angle-bracket and
 * square-bracket notations emitted by the schema.
 */
export const ARRAY_TYPE_RE = /^array[<[]([\w]+)[>\]]$/;
